import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import apiClient, { resolveApiBaseUrl } from '@/api/client'
import {
  nativeRiderTrackingState,
  nativeRiderTrackingSupported,
  nativeStartRiderTracking,
  nativeStopRiderTracking,
} from '@/lib/bhooklyNative'
import {
  addPoint,
  HEARTBEAT_MS,
  pointFromPosition,
  SEND_INTERVAL_MS,
} from '@/lib/riderLocationBuffer'
import type { RiderLocationPoint } from '@/types/rider'

// Delivery riders, phase 2 (F-DLV-N2): while the rider is on shift, share the
// phone's position. Fixes are buffered and sent every 15s (kept through a
// dropped connection); a parked phone is asked for a fix every 30s so the
// counter's "last seen" stays honest; the screen is kept awake, because a web
// page only gets GPS while it is open and visible. Off shift: nothing runs.

export type LocationStatus = 'off' | 'starting' | 'sharing' | 'denied' | 'unavailable' | 'unsupported'

export interface RiderLocationSharing {
  status: LocationStatus
  /** 'native' = the Android app's background service; 'web' = this page's GPS. */
  mode: 'web' | 'native'
  /** Native only: "Allow all the time" granted, so the screen may go off. */
  background: boolean
  lastSentAt: number | null
  lastFixAt: number | null
  accuracyM: number | null
  /** Screen wake lock held (the browser may refuse it). */
  awake: boolean
}

interface BatteryManagerLike {
  level: number
}

export function useRiderLocationSharing(onShift: boolean): RiderLocationSharing {
  const [native] = useState(() => nativeRiderTrackingSupported())
  const nativeState = useNativeRiderTracking(native && onShift, native)
  const webState = useWebRiderTracking(!native && onShift)
  return native ? nativeState : webState
}

// Inside the Android app (agent ≥ 1.5.0, F-DLV-N3): the app's foreground
// service reads GPS and posts it, screen on or off. This page only starts it
// (with a fresh session token), stops it off shift, and shows its state.
const NATIVE_POLL_MS = 5_000
const NATIVE_TOKEN_REFRESH_MS = 10 * 60_000

function useNativeRiderTracking(onShift: boolean, enabled: boolean): RiderLocationSharing {
  const [state, setState] = useState<RiderLocationSharing>({
    status: 'off',
    mode: 'native',
    background: false,
    lastSentAt: null,
    lastFixAt: null,
    accuracyM: null,
    awake: true,
  })
  useEffect(() => {
    if (!enabled) return
    if (!onShift) {
      nativeStopRiderTracking()
      setState((s) => ({ ...s, status: 'off' }))
      return
    }
    const start = () => {
      const token = apiClient.getAuthToken()
      const base = new URL(resolveApiBaseUrl(), window.location.origin).href
      if (token) nativeStartRiderTracking(base, token)
    }
    const read = () => {
      const s = nativeRiderTrackingState()
      if (!s) return
      setState((prev) => ({
        ...prev,
        status: !s.fine ? 'denied' : s.running ? 'sharing' : 'starting',
        background: s.background,
        lastSentAt: s.last_sent_at > 0 ? s.last_sent_at : null,
      }))
    }
    start()
    read()
    const poll = window.setInterval(read, NATIVE_POLL_MS)
    const refresh = window.setInterval(start, NATIVE_TOKEN_REFRESH_MS)
    return () => {
      window.clearInterval(poll)
      window.clearInterval(refresh)
    }
  }, [onShift, enabled])
  return state
}

function useWebRiderTracking(onShift: boolean): RiderLocationSharing {
  const qc = useQueryClient()
  const [status, setStatus] = useState<LocationStatus>('off')
  const [lastSentAt, setLastSentAt] = useState<number | null>(null)
  const [lastFixAt, setLastFixAt] = useState<number | null>(null)
  const [accuracyM, setAccuracyM] = useState<number | null>(null)
  const [awake, setAwake] = useState(false)
  const buffer = useRef<RiderLocationPoint[]>([])
  const sending = useRef(false)

  useEffect(() => {
    if (!onShift) {
      setStatus('off')
      buffer.current = []
      return
    }
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      setStatus('unsupported')
      return
    }
    setStatus('starting')
    let stopped = false
    let lastFix = 0

    const onFix = (pos: GeolocationPosition) => {
      if (stopped) return
      const p = pointFromPosition(pos)
      buffer.current = addPoint(buffer.current, p)
      lastFix = Date.now()
      setLastFixAt(lastFix)
      setAccuracyM(p.accuracy_m ?? null)
      setStatus('sharing')
    }
    const onError = (err: GeolocationPositionError) => {
      if (stopped) return
      if (err.code === err.PERMISSION_DENIED) setStatus('denied')
      else if (!lastFix) setStatus('unavailable')
    }
    const opts: PositionOptions = { enableHighAccuracy: true, maximumAge: 10_000, timeout: 20_000 }
    const watchId = navigator.geolocation.watchPosition(onFix, onError, opts)

    const send = async () => {
      if (stopped || sending.current) return
      if (Date.now() - lastFix > HEARTBEAT_MS) {
        navigator.geolocation.getCurrentPosition(onFix, onError, opts)
      }
      if (buffer.current.length === 0) return
      const batch = buffer.current
      sending.current = true
      try {
        let battery: number | null = null
        const nav = navigator as Navigator & { getBattery?: () => Promise<BatteryManagerLike> }
        if (nav.getBattery) battery = (await nav.getBattery().catch(() => null))?.level ?? null
        await apiClient.postRiderLocation(batch, battery)
        // Only drop what was sent — fixes that arrived meanwhile stay queued.
        buffer.current = buffer.current.filter((p) => !batch.includes(p))
        setLastSentAt(Date.now())
      } catch (err) {
        const code = (err as { code?: string })?.code
        if (code === 'rider_off_shift') {
          buffer.current = []
          qc.invalidateQueries({ queryKey: ['rider', 'me'] })
        }
        // Otherwise keep the buffer and try again next tick (offline, 5xx).
      } finally {
        sending.current = false
      }
    }
    const timer = window.setInterval(send, SEND_INTERVAL_MS)

    // Keep the screen on while on shift; re-acquire when the page comes back.
    let lock: { release: () => Promise<void>; addEventListener?: (t: string, cb: () => void) => void } | null = null
    const acquire = async () => {
      const wl = (navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<typeof lock> } }).wakeLock
      if (!wl || document.visibilityState !== 'visible' || stopped) return
      try {
        lock = await wl.request('screen')
        setAwake(true)
        lock?.addEventListener?.('release', () => setAwake(false))
      } catch {
        setAwake(false)
      }
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void acquire()
        void send()
      }
    }
    void acquire()
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      stopped = true
      navigator.geolocation.clearWatch(watchId)
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
      void lock?.release().catch(() => undefined)
      setAwake(false)
    }
  }, [onShift, qc])

  return { status, mode: 'web', background: false, lastSentAt, lastFixAt, accuracyM, awake }
}
